// Role-Based Access Control service
import { supabase } from './supabase';
import { noderr, DatabaseError, ValidationError } from '../../shared/utils/errorHandler';
import type { 
  Role, 
  Permission, 
  UserRole, 
  UserPermissions, 
  RoleWithPermissions,
  UserWithRoles,
  PermissionCheck 
} from '../../shared/types/auth';
import type {
  UserProfile,
  UserActivity,
  UserSession,
  FeatureUsage,
  UserInvitation,
  NotificationPreference,
  EnhancedUserWithRoles,
  UserSearchFilters,
  BulkUserOperation,
  BulkOperationResult,
  UserEngagementMetrics,
  CreateUserForm,
  UpdateUserForm,
  FeatureName,
  NotificationType
} from '../../shared/types/user-management';
import type {
  RoleTemplate,
  PermissionGroup,
  RoleAnalytics,
  RoleUsageStats,
  PermissionUpdate,
  BulkRoleOperation,
  BulkRoleOperationResult,
  ROLE_TEMPLATES,
  PERMISSION_GROUPS
} from '../../shared/types/role-management';

export class RBACService {
  // Role management
  async getRoles(): Promise<Role[]> {
    try {
      // Try to use the API endpoint first (server-side with service role)
      const response = await fetch('/api/roles');
      if (response.ok) {
        const roles = await response.json();
        console.debug('RBACService.getRoles returning from API:', roles.length, 'roles');
        return roles;
      } else {
        console.warn('Roles API endpoint failed, falling back to direct query');
      }
    } catch (apiError) {
      console.warn('Roles API endpoint not available, falling back to direct query:', apiError);
    }

    // Fallback: Direct database query (this will likely fail due to RLS)
    try {
      const [error, result] = await noderr(
        supabase
          .from('roles')
          .select('*')
          .eq('is_active', true)
          .order('name')
      );
      
      if (error) throw new DatabaseError(`Failed to fetch roles: ${error.message}`);
      return (result as any)?.data || [];
    } catch (fallbackError) {
      console.error('Direct database query also failed:', fallbackError);
      // Return empty array as last resort
      return [];
    }
  }

  async getRoleById(id: string): Promise<Role | null> {
    const [error, result] = await noderr(
      supabase
        .from('roles')
        .select('*')
        .eq('id', id)
        .single()
    );
    
    if (error) throw new DatabaseError(`Failed to fetch role: ${error.message}`);
    return (result as any)?.data || null;
  }

  async createRole(role: Omit<Role, 'id' | 'created_at' | 'updated_at'>): Promise<Role> {
    const [error, result] = await noderr(
      supabase
        .from('roles')
        .insert(role)
        .select()
        .single()
    );
    
    if (error) throw new DatabaseError(`Failed to create role: ${error.message}`);
    return (result as any)?.data;
  }

  // Permission management
  async getPermissions(): Promise<Permission[]> {
    const [error, result] = await noderr(
      supabase
        .from('permissions')
        .select('*')
        .order('resource', { ascending: true })
        .order('action', { ascending: true })
    );
    
    if (error) throw new DatabaseError(`Failed to fetch permissions: ${error.message}`);
    return (result as any)?.data || [];
  }

  async getRolePermissions(roleId: string): Promise<Permission[]> {
    const [error, result] = await noderr(
      supabase
        .from('role_permissions')
        .select(`
          permission:permissions(*)
        `)
        .eq('role_id', roleId)
    );
    
    if (error) throw new DatabaseError(`Failed to fetch role permissions: ${error.message}`);
    return (result as any)?.data?.map((rp: any) => rp.permission) || [];
  }

  async assignPermissionToRole(roleId: string, permissionId: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('role_permissions')
        .insert({ role_id: roleId, permission_id: permissionId })
    );
    
    if (error) throw new DatabaseError(`Failed to assign permission to role: ${error.message}`);
  }

  async removePermissionFromRole(roleId: string, permissionId: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('role_permissions')
        .delete()
        .eq('role_id', roleId)
        .eq('permission_id', permissionId)
    );
    
    if (error) throw new DatabaseError(`Failed to remove permission from role: ${error.message}`);
  }

  // User role management
  async getUserRoles(userId: string): Promise<Role[]> {
    try {
      // Try to use the API endpoint first (server-side with service role)
      const response = await fetch(`/api/user-roles-get?userId=${userId}`);
      if (response.ok) {
        const data = await response.json();
        console.debug('RBACService.getUserRoles returning from API:', data.roles.length, 'roles');
        return data.roles || [];
      } else {
        console.warn('User roles API endpoint failed, falling back to direct query');
      }
    } catch (apiError) {
      console.warn('User roles API endpoint not available, falling back to direct query:', apiError);
    }

    // Fallback: Direct database query (this will likely fail due to RLS)
    try {
      const [error, result] = await noderr(
        supabase
          .from('user_roles')
          .select(`
            role:roles(*)
          `)
          .eq('user_id', userId)
      );
      
      if (error) throw new DatabaseError(`Failed to fetch user roles: ${error.message}`);
      return (result as any)?.data?.map((ur: any) => ur.role) || [];
    } catch (fallbackError) {
      console.error('Direct database query also failed:', fallbackError);
      // Return empty array as last resort
      return [];
    }
  }

  async getUserPermissions(userId: string): Promise<UserPermissions[]> {
    try {
      // Try to use the API endpoint first (server-side with service role)
      const response = await fetch(`/api/user-roles-get?userId=${userId}`);
      if (response.ok) {
        const data = await response.json();
        console.debug('RBACService.getUserPermissions returning from API:', data.permissions.length, 'permissions');
        return data.permissions || [];
      } else {
        console.warn('User permissions API endpoint failed, falling back to direct query');
      }
    } catch (apiError) {
      console.warn('User permissions API endpoint not available, falling back to direct query:', apiError);
    }

    // Fallback: Direct database query (this will likely fail due to RLS)
    try {
      const [error, result] = await noderr(
        supabase
          .from('user_permissions')
          .select('*')
          .eq('user_id', userId)
      );
      
      if (error) throw new DatabaseError(`Failed to fetch user permissions: ${error.message}`);
      return (result as any)?.data || [];
    } catch (fallbackError) {
      console.error('Direct database query also failed:', fallbackError);
      // Return empty array as last resort
      return [];
    }
  }

  async assignRoleToUser(userId: string, roleId: string, assignedBy?: string): Promise<void> {
    try {
      // Try to use the API endpoint first (server-side with service role)
      const response = await fetch('/api/user-roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, roleId }),
      });

      if (response.ok) {
        console.debug('RBACService.assignRoleToUser: Role assigned via API');
        return;
      } else {
        console.warn('User roles API endpoint failed, falling back to direct query');
      }
    } catch (apiError) {
      console.warn('User roles API endpoint not available, falling back to direct query:', apiError);
    }

    // Fallback: Direct database query
    const [error] = await noderr(
      supabase
        .from('user_roles')
        .insert({ 
          user_id: userId, 
          role_id: roleId, 
          assigned_by: assignedBy 
        })
    );
    
    if (error) throw new DatabaseError(`Failed to assign role to user: ${error.message}`);
  }

  async removeRoleFromUser(userId: string, roleId: string): Promise<void> {
    try {
      // Try to use the API endpoint first (server-side with service role)
      const response = await fetch(`/api/user-roles?userId=${userId}&roleId=${roleId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        console.debug('RBACService.removeRoleFromUser: Role removed via API');
        return;
      } else {
        console.warn('User roles API endpoint failed, falling back to direct query');
      }
    } catch (apiError) {
      console.warn('User roles API endpoint not available, falling back to direct query:', apiError);
    }

    // Fallback: Direct database query
    const [error] = await noderr(
      supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role_id', roleId)
    );
    
    if (error) throw new DatabaseError(`Failed to remove role from user: ${error.message}`);
  }

  // Permission checking
  async hasPermission(userId: string, resource: string, action: string): Promise<boolean> {
    const [error, result] = await noderr(
      supabase
        .from('user_permissions')
        .select('permission_name')
        .eq('user_id', userId)
        .eq('resource', resource)
        .eq('action', action)
        .limit(1)
    );
    
    if (error) {
      console.error('Permission check failed:', error);
      return false;
    }
    
    return ((result as any)?.data?.length || 0) > 0;
  }

  async hasRole(userId: string, roleName: string): Promise<boolean> {
    const [error, result] = await noderr(
      supabase
        .from('user_roles')
        .select(`
          role:roles!inner(name)
        `)
        .eq('user_id', userId)
        .eq('role.name', roleName)
        .limit(1)
    );
    
    if (error) {
      console.error('Role check failed:', error);
      return false;
    }
    
    return ((result as any)?.data?.length || 0) > 0;
  }

  // Get all users with their roles for admin proxy functionality
  async getAllUsersWithRoles(): Promise<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    roles: Role[];
  }>> {
    const [error, result] = await noderr(
      supabase
        .from('user_profiles')
        .select(`
          id,
          first_name,
          last_name,
          user_roles(
            role:roles(*)
          )
        `)
    );
    
    if (error) throw new DatabaseError(`Failed to fetch users with roles: ${error.message}`);
    
    // Transform the data to flatten the structure
    return ((result as any)?.data || []).map((user: any) => ({
      id: user.id,
      email: user.email || 'No email', // We'll need to get email from auth.users
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      roles: user.user_roles?.map((ur: any) => ur.role).filter(Boolean) || []
    }));
  }

  // Utility methods
  async getUserWithRoles(userId: string): Promise<UserWithRoles | null> {
    const [userError, userResult] = await noderr(
      supabase.auth.admin.getUserById(userId)
    );
    
    if (userError || !(userResult as any)?.data?.user) {
      return null;
    }

    const roles = await this.getUserRoles(userId);
    const permissions = await this.getUserPermissions(userId);
    
    return {
      id: (userResult as any).data.user.id,
      email: (userResult as any).data.user.email || '',
      roles,
      permissions: permissions.map(p => ({
        id: '',
        name: p.permission_name,
        resource: p.resource,
        action: p.action,
        description: p.description,
        created_at: '',
        updated_at: ''
      }))
    };
  }

  async getRoleWithPermissions(roleId: string): Promise<RoleWithPermissions | null> {
    const role = await this.getRoleById(roleId);
    if (!role) return null;

    const permissions = await this.getRolePermissions(roleId);
    
    return {
      ...role,
      permissions
    };
  }

  // Enhanced user management methods
  async getAllUsers(): Promise<UserWithRoles[]> {
    console.debug('RBACService.getAllUsers called');
    
    try {
      // Try to use the API endpoint first (server-side with service role)
      try {
        const response = await fetch('/api/users-with-auth');
        if (response.ok) {
          const users = await response.json();
          console.debug('RBACService.getAllUsers returning from API:', users.length, 'users');
          return users;
        } else {
          console.warn('API endpoint failed, falling back to direct query');
        }
      } catch (apiError) {
        console.warn('API endpoint not available, falling back to direct query:', apiError);
      }

      // Fallback: Get user profiles with all available columns
      const [profilesError, profilesResult] = await noderr(
        supabase
          .from('user_profiles')
          .select(`
            id,
            first_name,
            last_name,
            status,
            last_activity_at,
            created_at,
            updated_at
          `)
          .order('created_at', { ascending: false })
      );

      if (profilesError) {
        console.error('Failed to fetch user profiles:', profilesError);
        return [];
      }

      console.debug('Fetched profiles:', (profilesResult as any)?.data?.length || 0);

      // Try to get auth users for email and sign-in information
      let authUserMap = new Map();
      try {
        const [authError, authResult] = await noderr(
          supabase.auth.admin.listUsers()
        );

        if (authError) {
          console.warn('Failed to fetch auth users (this is expected in some environments):', authError.message);
        } else {
          const authUsers = (authResult as any)?.data?.users || [];
          authUserMap = new Map(authUsers.map((user: any) => [user.id, user]));
          console.debug('Fetched auth users:', authUsers.length);
        }
      } catch (authError) {
        console.warn('Auth admin API not available (this is expected in some environments):', authError);
      }

      // Combine profile and auth data
      const users = await Promise.all(
        ((profilesResult as any)?.data || []).map(async (profile: any) => {
          const authUser = authUserMap.get(profile.id);
          const roles = await this.getUserRoles(profile.id);
          
          // Determine status based on profile status and email confirmation
          let userStatus = profile.status || 'active';
          if (authUser && !(authUser as any).email_confirmed_at) {
            userStatus = 'pending';
          }
          
          // Try to get email from auth user, fallback to a placeholder
          let email = 'No email';
          if (authUser && (authUser as any).email) {
            email = (authUser as any).email;
          } else {
            // If we can't get email from auth, try to construct a placeholder
            const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
            if (name) {
              email = `${name.toLowerCase().replace(/\s+/g, '.')}@example.com`;
            } else {
              email = `user-${profile.id.substring(0, 8)}@example.com`;
            }
          }
          
          return {
            id: profile.id,
            email,
            first_name: profile.first_name || '',
            last_name: profile.last_name || '',
            roles,
            permissions: [],
            created_at: profile.created_at,
            updated_at: profile.updated_at || profile.created_at,
            last_sign_in_at: (authUser as any)?.last_sign_in_at,
            email_confirmed_at: (authUser as any)?.email_confirmed_at,
            status: userStatus
          };
        })
      );

      console.debug('RBACService.getAllUsers returning:', users.length, 'users');
      return users;
    } catch (error) {
      console.error('Error in getAllUsers:', error);
      return [];
    }
  }


  // Enhanced user management methods
  async getEnhancedUsers(filters?: UserSearchFilters): Promise<EnhancedUserWithRoles[]> {
    // Use basic query instead of the view to avoid 403 errors
    const [profilesError, profilesResult] = await noderr(
      supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false })
    );

    if (profilesError) throw new DatabaseError(`Failed to fetch enhanced users: ${profilesError.message}`);

    let users = ((profilesResult as any)?.data || []).map((user: any) => ({
      id: user.id,
      email: user.email || 'No email',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      status: user.status || 'active',
      timezone: user.timezone || 'UTC',
      phone: user.phone,
      avatar_url: user.avatar_url,
      profile_completeness: user.profile_completeness || 0,
      last_activity_at: user.last_activity_at,
      roles: Array.isArray(user.roles) ? user.roles : [],
      permissions: [],
      clients: [],
      created_at: user.created_at,
      updated_at: user.updated_at || user.created_at,
      last_sign_in_at: user.last_sign_in_at,
      email_confirmed_at: user.email_confirmed_at,
      activity_count_30d: user.activity_count_30d || 0
    }));

    // Apply filters if provided
    if (filters) {
      users = this.applyUserFilters(users, filters);
    }

    return users;
  }

  private applyUserFilters(users: EnhancedUserWithRoles[], filters: UserSearchFilters): EnhancedUserWithRoles[] {
    return users.filter(user => {
      // Basic filters
      if (filters.basic.name) {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        if (!fullName.includes(filters.basic.name.toLowerCase())) {
          return false;
        }
      }

      if (filters.basic.email) {
        if (!user.email.toLowerCase().includes(filters.basic.email.toLowerCase())) {
          return false;
        }
      }

      if (filters.basic.role && filters.basic.role.length > 0) {
        const userRoleNames = user.roles.map(r => r.name);
        if (!filters.basic.role.some(role => userRoleNames.includes(role))) {
          return false;
        }
      }

      if (filters.basic.status && filters.basic.status.length > 0) {
        if (!filters.basic.status.includes(user.status)) {
          return false;
        }
      }

      // Advanced filters
      if (filters.advanced.profile_completeness_min) {
        if (user.profile_completeness < filters.advanced.profile_completeness_min) {
          return false;
        }
      }

      if (filters.advanced.last_login_range) {
        if (user.last_sign_in_at) {
          const lastLogin = new Date(user.last_sign_in_at);
          const start = new Date(filters.advanced.last_login_range.start);
          const end = new Date(filters.advanced.last_login_range.end);
          if (lastLogin < start || lastLogin > end) {
            return false;
          }
        }
      }

      return true;
    });
  }

  // Activity logging
  async logUserActivity(
    userId: string,
    actionType: string,
    resourceType?: string,
    resourceId?: string,
    resourceName?: string,
    details: Record<string, any> = {},
    ipAddress?: string,
    userAgent?: string
  ): Promise<string> {
    const [error, result] = await noderr(
      supabase.rpc('log_user_activity', {
        p_user_id: userId,
        p_action_type: actionType,
        p_resource_type: resourceType,
        p_resource_id: resourceId,
        p_resource_name: resourceName,
        p_details: details,
        p_ip_address: ipAddress,
        p_user_agent: userAgent
      })
    );

    if (error) throw new DatabaseError(`Failed to log user activity: ${error.message}`);
    return (result as any)?.data || '';
  }

  // Feature usage tracking
  async trackFeatureUsage(userId: string, featureName: FeatureName): Promise<void> {
    const [error] = await noderr(
      supabase.rpc('track_feature_usage', {
        p_user_id: userId,
        p_feature_name: featureName
      })
    );

    if (error) throw new DatabaseError(`Failed to track feature usage: ${error.message}`);
  }

  // User activities
  async getUserActivities(userId: string, limit: number = 50): Promise<UserActivity[]> {
    const [error, result] = await noderr(
      supabase
        .from('user_activities')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit)
    );

    if (error) throw new DatabaseError(`Failed to fetch user activities: ${error.message}`);
    return (result as any)?.data || [];
  }

  // User sessions
  async getUserSessions(userId: string): Promise<UserSession[]> {
    const [error, result] = await noderr(
      supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('last_activity_at', { ascending: false })
    );

    if (error) throw new DatabaseError(`Failed to fetch user sessions: ${error.message}`);
    return (result as any)?.data || [];
  }

  // Notification preferences
  async getUserNotificationPreferences(userId: string): Promise<NotificationPreference[]> {
    const [error, result] = await noderr(
      supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .order('notification_type')
    );

    if (error) throw new DatabaseError(`Failed to fetch notification preferences: ${error.message}`);
    return (result as any)?.data || [];
  }

  async updateNotificationPreference(
    userId: string,
    notificationType: NotificationType,
    preferences: Partial<NotificationPreference>
  ): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('user_notification_preferences')
        .upsert({
          user_id: userId,
          notification_type: notificationType,
          ...preferences,
          updated_at: new Date().toISOString()
        })
    );

    if (error) throw new DatabaseError(`Failed to update notification preference: ${error.message}`);
  }

  // Bulk operations
  async performBulkUserOperation(operation: BulkUserOperation): Promise<BulkOperationResult> {
    const result: BulkOperationResult = {
      success_count: 0,
      failure_count: 0,
      errors: []
    };

    for (const userId of operation.user_ids) {
      try {
        switch (operation.type) {
          case 'status_change':
            await this.updateUserProfile(userId, {
              status: operation.parameters.status,
              suspension_reason: operation.parameters.suspension_reason,
              suspension_expires_at: operation.parameters.suspension_expires_at
            });
            break;
          
          case 'role_change':
            // Remove existing roles
            const currentRoles = await this.getUserRoles(userId);
            for (const role of currentRoles) {
              await this.removeRoleFromUser(userId, role.id);
            }
            // Add new roles
            for (const roleId of operation.parameters.role_ids) {
              await this.assignRoleToUser(userId, roleId);
            }
            break;
          
          case 'password_reset':
            await this.resetUserPassword(userId);
            break;
          
          case 'delete':
            await this.deleteUser(userId);
            break;
        }
        result.success_count++;
      } catch (error) {
        result.failure_count++;
        result.errors.push({
          user_id: userId,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    return result;
  }

  // User analytics
  async getUserEngagementMetrics(): Promise<UserEngagementMetrics> {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Get daily active users
    const [dauError, dauResult] = await noderr(
      supabase
        .from('user_activities')
        .select('user_id')
        .gte('created_at', oneDayAgo.toISOString())
        .distinct()
    );

    // Get weekly active users
    const [wauError, wauResult] = await noderr(
      supabase
        .from('user_activities')
        .select('user_id')
        .gte('created_at', oneWeekAgo.toISOString())
        .distinct()
    );

    // Get monthly active users
    const [mauError, mauResult] = await noderr(
      supabase
        .from('user_activities')
        .select('user_id')
        .gte('created_at', oneMonthAgo.toISOString())
        .distinct()
    );

    // Get feature usage stats
    const [featureError, featureResult] = await noderr(
      supabase
        .from('feature_usage')
        .select('feature_name, usage_count')
    );

    const featureStats: Record<string, number> = {};
    if ((featureResult as any)?.data) {
      for (const usage of (featureResult as any).data) {
        featureStats[usage.feature_name] = (featureStats[usage.feature_name] || 0) + usage.usage_count;
      }
    }

    return {
      daily_active_users: (dauResult as any)?.data?.length || 0,
      weekly_active_users: (wauResult as any)?.data?.length || 0,
      monthly_active_users: (mauResult as any)?.data?.length || 0,
      average_session_duration: 0, // Would need session tracking
      feature_usage_stats: featureStats,
      user_retention_rate: 0, // Would need cohort analysis
      churn_risk_users: [] // Would need ML analysis
    };
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const response = await fetch(`/api/user-profile?userId=${userId}`);
      if (response.ok) {
        const profile = await response.json();
        return profile;
      }
      
      // Fallback to direct Supabase query
      const result = await noderr(supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()
      );
      
      if (result.error) {
        console.error('Error fetching user profile:', result.error);
        return null;
      }
      
      return result.data;
    } catch (error) {
      console.error('Error in getUserProfile:', error);
      return null;
    }
  }

  async updateUserProfile(userId: string, updates: UpdateUserForm): Promise<void> {
    try {
      // Try to use the API endpoint first (server-side with service role)
      const response = await fetch('/api/user-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, updates }),
      });

      if (response.ok) {
        console.debug('RBACService.updateUserProfile: Profile updated via API');
        return;
      } else {
        console.warn('User profile API endpoint failed, falling back to direct query');
      }
    } catch (apiError) {
      console.warn('User profile API endpoint not available, falling back to direct query:', apiError);
    }

    // Fallback: Direct database query (this will likely fail due to RLS)
    try {
      const [error] = await noderr(
        supabase
          .from('user_profiles')
          .update(updates)
          .eq('id', userId)
      );
      
      if (error) throw new DatabaseError(`Failed to update user profile: ${error.message}`);
    } catch (fallbackError) {
      console.error('Direct database query also failed:', fallbackError);
      throw new DatabaseError(`Failed to update user profile: ${fallbackError.message}`);
    }
  }

  async deleteUser(userId: string): Promise<void> {
    const [error] = await noderr(
      supabase.auth.admin.deleteUser(userId)
    );
    
    if (error) throw new DatabaseError(`Failed to delete user: ${error.message}`);
  }

  async inviteUser(email: string, roleIds: string[], invitedBy: string): Promise<void> {
    try {
      // Try to use the API endpoint first (server-side with service role)
      const response = await fetch('/api/invite-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, roleIds, invitedBy }),
      });

      if (response.ok) {
        console.debug('RBACService.inviteUser: User invited via API');
        return;
      } else {
        console.warn('Invite user API endpoint failed, falling back to direct query');
      }
    } catch (apiError) {
      console.warn('Invite user API endpoint not available, falling back to direct query:', apiError);
    }

    // Fallback: Direct database query (this will likely fail due to RLS)
    try {
      const [error] = await noderr(
        supabase.auth.admin.inviteUserByEmail(email, {
          data: {
            invited_by: invitedBy,
            assigned_roles: roleIds
          }
        })
      );
      
      if (error) throw new DatabaseError(`Failed to invite user: ${error.message}`);
    } catch (fallbackError) {
      console.error('Direct database query also failed:', fallbackError);
      throw new DatabaseError(`Failed to invite user: ${fallbackError.message}`);
    }
  }

  async suspendUser(userId: string, suspended: boolean): Promise<void> {
    try {
      // Try to use the API endpoint first (server-side with service role)
      const action = suspended ? 'suspend' : 'activate';
      const response = await fetch('/api/user-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          action, 
          userId,
          reason: suspended ? 'Suspended by administrator' : undefined
        }),
      });

      if (response.ok) {
        console.debug(`RBACService.suspendUser: User ${action}ed via API`);
        return;
      } else {
        console.warn('User management API endpoint failed, falling back to direct query');
      }
    } catch (apiError) {
      console.warn('User management API endpoint not available, falling back to direct query:', apiError);
    }

    // Fallback: Direct database query (this will likely fail due to RLS)
    try {
      const [error] = await noderr(
        supabase.auth.admin.updateUserById(userId, {
          ban_duration: suspended ? '876000h' : 'none' // 100 years if suspended
        })
      );
      
      if (error) throw new DatabaseError(`Failed to ${suspended ? 'suspend' : 'unsuspend'} user: ${error.message}`);
    } catch (fallbackError) {
      console.error('Direct database query also failed:', fallbackError);
      throw new DatabaseError(`Failed to ${suspended ? 'suspend' : 'unsuspend'} user: ${fallbackError.message}`);
    }
  }

  async resetUserPassword(userId: string): Promise<void> {
    const [error] = await noderr(
      supabase.auth.admin.generateLink({
        type: 'recovery',
        email: '', // We'd need to get the email first
        options: {
          redirectTo: `${window.location.origin}/reset-password`
        }
      })
    );
    
    if (error) throw new DatabaseError(`Failed to reset user password: ${error.message}`);
  }

  // Role management enhancements
  async updateRole(roleId: string, updates: Partial<Role>): Promise<Role> {
    const [error, result] = await noderr(
      supabase
        .from('roles')
        .update(updates)
        .eq('id', roleId)
        .select()
        .single()
    );
    
    if (error) throw new DatabaseError(`Failed to update role: ${error.message}`);
    return (result as any)?.data;
  }

  async deleteRole(roleId: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('roles')
        .delete()
        .eq('id', roleId)
    );
    
    if (error) throw new DatabaseError(`Failed to delete role: ${error.message}`);
  }

  async getUsersByRole(roleId: string): Promise<UserWithRoles[]> {
    const [error, result] = await noderr(
      supabase
        .from('user_roles')
        .select(`
          user_id,
          user_profiles!inner(
            id,
            first_name,
            last_name,
            created_at,
            updated_at
          )
        `)
        .eq('role_id', roleId)
    );
    
    if (error) throw new DatabaseError(`Failed to fetch users by role: ${error.message}`);
    
    // Get email addresses and roles for each user
    const userIds = ((result as any)?.data || []).map((ur: any) => ur.user_id);
    const [authError, authResult] = await noderr(
      supabase.auth.admin.listUsers()
    );
    
    if (authError) {
      console.warn('Failed to fetch auth user data:', authError);
    }
    
    const authUsers = (authResult as any)?.data?.users || [];
    const authUserMap = new Map(authUsers.map((user: any) => [user.id, user]));
    
    const usersWithRoles = await Promise.all(
      userIds.map(async (userId: string) => {
        const userProfile = (result as any)?.data?.find((ur: any) => ur.user_id === userId)?.user_profiles;
        const authUser = authUserMap.get(userId);
        const roles = await this.getUserRoles(userId);
        
        return {
          id: userId,
          email: (authUser as any)?.email || 'No email',
          first_name: userProfile?.first_name || '',
          last_name: userProfile?.last_name || '',
          roles,
          created_at: userProfile?.created_at,
          updated_at: userProfile?.updated_at,
          last_sign_in_at: (authUser as any)?.last_sign_in_at,
          email_confirmed_at: (authUser as any)?.email_confirmed_at
        };
      })
    );
    
    return usersWithRoles;
  }

  // Auto-assign admin role to first user
  async ensureAdminUser(userId: string): Promise<void> {
    // Check if any admin users exist
    const [error, result] = await noderr(
      supabase
        .from('user_roles')
        .select(`
          role:roles!inner(name)
        `)
        .eq('role.name', 'admin')
        .limit(1)
    );

    if (error) {
      console.error('Failed to check for admin users:', error);
      return;
    }

    // If no admin users exist, make this user an admin
    if (!(result as any)?.data || (result as any).data.length === 0) {
      const [roleError, roleResult] = await noderr(
        supabase
          .from('roles')
          .select('id')
          .eq('name', 'admin')
          .single()
      );

      if (!roleError && (roleResult as any)?.data) {
        await this.assignRoleToUser(userId, (roleResult as any).data.id);
        console.log('Auto-assigned admin role to first user');
      }
    }
  }

  // Enhanced Role Management Methods
  async getRoleTemplates(): Promise<RoleTemplate[]> {
    // For now, return the hardcoded templates
    // In the future, these could be stored in the database
    const { ROLE_TEMPLATES } = await import('../../shared/types/role-management');
    return Object.entries(ROLE_TEMPLATES).map(([key, template], index) => ({
      id: `template_${index + 1}`,
      ...template,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
  }

  async createRoleFromTemplate(templateId: string, customizations: Partial<Role>): Promise<Role> {
    const templates = await this.getRoleTemplates();
    const template = templates.find(t => t.id === templateId);
    
    if (!template) {
      throw new ValidationError('Template not found');
    }

    // Create the role
    const role = await this.createRole({
      name: customizations.name || template.name.toLowerCase().replace(/\s+/g, '_'),
      description: customizations.description || template.description,
      is_active: true
    });

    // Assign template permissions
    const allPermissions = await this.getPermissions();
    for (const permissionName of template.permissions) {
      const permission = allPermissions.find(p => 
        p.name === permissionName || 
        `${p.resource}:${p.action}` === permissionName
      );
      if (permission) {
        await this.assignPermissionToRole(role.id, permission.id);
      }
    }

    return role;
  }

  async getRoleAnalytics(): Promise<RoleAnalytics> {
    const roles = await this.getRoles();
    const allUsers = await this.getAllUsers();
    const allPermissions = await this.getPermissions();

    // Calculate users by role
    const usersByRole: Record<string, number> = {};
    for (const user of allUsers) {
      for (const role of user.roles) {
        usersByRole[role.name] = (usersByRole[role.name] || 0) + 1;
      }
    }

    // Calculate permission coverage
    const totalPossiblePermissions = roles.length * allPermissions.length;
    let assignedPermissions = 0;
    for (const role of roles) {
      const rolePermissions = await this.getRolePermissions(role.id);
      assignedPermissions += rolePermissions.length;
    }
    const permissionCoverage = totalPossiblePermissions > 0 
      ? Math.round((assignedPermissions / totalPossiblePermissions) * 100) 
      : 0;

    // Mock permission usage data (would be real in production)
    const mostUsedPermissions = allPermissions.slice(0, 5).map((p, i) => ({
      permission_id: p.id,
      permission_name: p.name,
      usage_count: 100 - (i * 15)
    }));

    const leastUsedPermissions = allPermissions.slice(-5).map((p, i) => ({
      permission_id: p.id,
      permission_name: p.name,
      usage_count: 5 + i
    }));

    // Calculate role distribution
    const totalUsers = allUsers.length;
    const roleDistribution = Object.entries(usersByRole).map(([roleName, count]) => ({
      role_name: roleName,
      user_count: count,
      percentage: totalUsers > 0 ? Math.round((count / totalUsers) * 100) : 0
    }));

    return {
      total_roles: roles.length,
      active_roles: roles.filter(r => r.is_active).length,
      users_by_role: usersByRole,
      permission_coverage: permissionCoverage,
      most_used_permissions: mostUsedPermissions,
      least_used_permissions: leastUsedPermissions,
      role_distribution: roleDistribution,
      security_score: Math.min(95, 60 + (permissionCoverage * 0.35)), // Mock security score
      recommendations: [
        'Consider reviewing unused permissions',
        'Ensure principle of least privilege',
        'Regular role access reviews recommended'
      ]
    };
  }

  async getRoleUsageStats(roleId: string): Promise<RoleUsageStats> {
    const role = await this.getRoleById(roleId);
    if (!role) {
      throw new ValidationError('Role not found');
    }

    const users = await this.getUsersByRole(roleId);
    const permissions = await this.getRolePermissions(roleId);

    return {
      role_id: roleId,
      role_name: role.name,
      user_count: users.length,
      permission_count: permissions.length,
      last_used: new Date().toISOString(), // Mock data
      activity_score: Math.min(100, users.length * 10 + permissions.length * 2),
      security_risk_level: permissions.length > 20 ? 'high' : permissions.length > 10 ? 'medium' : 'low',
      optimization_suggestions: [
        'Review permission assignments',
        'Consider role consolidation',
        'Audit user assignments'
      ]
    };
  }

  async getPermissionGroups(): Promise<PermissionGroup[]> {
    const { PERMISSION_GROUPS } = await import('../../shared/types/role-management');
    return Object.values(PERMISSION_GROUPS);
  }

  async bulkUpdatePermissions(roleId: string, permissions: PermissionUpdate[]): Promise<void> {
    for (const permission of permissions) {
      if (permission.action === 'grant') {
        await this.assignPermissionToRole(roleId, permission.permission_id);
      } else {
        await this.removePermissionFromRole(roleId, permission.permission_id);
      }
    }
  }

  async performBulkRoleOperation(operation: BulkRoleOperation): Promise<BulkRoleOperationResult> {
    const result: BulkRoleOperationResult = {
      success_count: 0,
      failure_count: 0,
      errors: []
    };

    for (const roleId of operation.role_ids) {
      try {
        switch (operation.type) {
          case 'permission_grant':
            if (operation.permission_ids) {
              for (const permissionId of operation.permission_ids) {
                await this.assignPermissionToRole(roleId, permissionId);
              }
            }
            break;
          
          case 'permission_revoke':
            if (operation.permission_ids) {
              for (const permissionId of operation.permission_ids) {
                await this.removePermissionFromRole(roleId, permissionId);
              }
            }
            break;
          
          case 'activate':
            await this.updateRole(roleId, { is_active: true });
            break;
          
          case 'deactivate':
            await this.updateRole(roleId, { is_active: false });
            break;
          
          case 'delete':
            await this.deleteRole(roleId);
            break;
        }
        result.success_count++;
      } catch (error) {
        result.failure_count++;
        result.errors.push({
          role_id: roleId,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    return result;
  }
}

// Export singleton instance
export const rbacService = new RBACService();
